import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Start cold start tracking FIRST (before any other work)
import { startColdStartTracking } from "./lib/perfMetrics";
startColdStartTracking();

// Install chunk error handlers FIRST, before any lazy loading
import { installChunkErrorHandlers } from "./utils/chunkErrorRecovery";
installChunkErrorHandlers();

// Install global crash reporter (window.onerror + unhandledrejection)
import { installCrashReporter } from "./lib/crashReporter";
installCrashReporter();

// DEV-only: inject no-transitions style if ?noTransitions=1 is present
import { injectNoTransitionsStyle } from "./hooks/useNoTransitionsFlag";
injectNoTransitionsStyle();

// Apply theme (system preference + persisted overrides) BEFORE first paint to avoid flash
import { initializeTheme } from "./lib/themeManager";
initializeTheme();

// Render the app immediately - don't block on non-critical tasks
const root = createRoot(document.getElementById("root")!);
root.render(<App />);

// Defer non-critical initialization to after first paint
const scheduleIdleTask = (callback: () => void) => {
  if ('requestIdleCallback' in window) {
    (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => number })
      .requestIdleCallback(callback, { timeout: 5000 });
  } else {
    setTimeout(callback, 100);
  }
};

scheduleIdleTask(() => {
  // Initialize cache manager and version checking
  import('./utils/cacheManager').then(({ cacheManager }) => {
    cacheManager.checkVersion();
    cacheManager.initUpdateChecker();
  });
  
  // Initialize web vitals monitoring
  import('./lib/webVitals').then(({ initWebVitals }) => {
    initWebVitals();
  });

  // Prewarm critical chunks for faster subsequent navigation
  import('./utils/cachePrewarmer').then(({ scheduleCachePrewarm }) => {
    scheduleCachePrewarm();
  });
  
  // Warm service worker caches for offline/cold-start performance
  import('./utils/swCacheWarmer').then(({ scheduleCacheWarming }) => {
    scheduleCacheWarming();
  });
});
